import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ExternalLink, Save, Loader2, Plus, X, Sparkles } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth, hasProfessionalAccess } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { api, friendlyError } from '../lib/api';
import { FormBanner, FormError } from '../components/FormError';
import useFormValidation from '../hooks/useFormValidation';
import ImageUploader from '../components/profile/ImageUploader';
import TagsField from '../components/profile/TagsField';
import CompletenessCard from '../components/profile/CompletenessCard';
import { KpiGrid, SeriesChart } from '../components/profile/MetricsDashboard';
import ActivityFeed from '../components/profile/ActivityFeed';

export default function AgentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (!hasProfessionalAccess(user)) {
      navigate('/mi-cuenta');
      return;
    }
    setLoading(true);
    api.getAgentMe()
      .then(setMe)
      .catch((e) => toast({ title: 'Error', description: friendlyError(e), variant: 'destructive' }))
      .finally(() => setLoading(false));
  }, [user, navigate, toast]);

  if (loading || !me) {
    return (
      <div className="min-h-screen bg-habita-cream">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-habita animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO title={`${me.name} · Mi perfil profesional`} noindex />
      <Header />
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3">
            {me.avatar
              ? <img src={me.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />
              : <div className="w-12 h-12 rounded-full bg-habita-mint flex items-center justify-center text-habita font-bold">{me.name?.charAt(0)}</div>}
            <div>
              <h1 className="font-serif text-2xl font-bold text-habita-dark">Mi perfil profesional</h1>
              <Link to={`/agente/${me.slug}`} className="text-xs text-habita hover:underline flex items-center gap-1" target="_blank" data-testid="agent-view-public-link">
                <ExternalLink className="w-3 h-3" /> Ver perfil público
              </Link>
            </div>
          </div>
          {me.agency && (
            <Link to={`/mi-cuenta/inmobiliaria`} className="text-xs text-habita-dark/70 hover:text-habita inline-flex items-center gap-1" data-testid="agent-my-agency-link">
              <Sparkles className="w-3 h-3" /> Miembro de {me.agency.name}
            </Link>
          )}
        </div>

        <AgentMetricsSection />
        <AgentEditForm initial={me} onSaved={(updated) => setMe(updated)} />
      </div>
      <Footer />
    </div>
  );
}

function AgentEditForm({ initial, onSaved }) {
  const { toast } = useToast();
  const v = useFormValidation();
  const [form, setForm] = useState({
    name: initial.name || '',
    title: initial.title || '',
    bio: initial.bio || '',
    history: initial.history || '',
    city: initial.city || '',
    years_experience: initial.years_experience ?? '',
    specialties: initial.specialties || [],
    cities_operating: initial.cities_operating || [],
    languages: initial.languages || [],
    avatar: initial.avatar || '',
    cover_url: initial.cover_url || '',
    gallery: initial.gallery || [],
    video_url: initial.video_url || '',
    whatsapp: initial.whatsapp || '',
    phone: initial.phone || '',
    email: initial.email || '',
    social: initial.social || {},
  });
  const [saving, setSaving] = useState(false);
  const [uploadingKind, setUploadingKind] = useState(null);
  const set = (k, val) => { setForm(f => ({ ...f, [k]: val })); v.clearField(k); };

  const upload = async (file, kind) => {
    if (!file) return;
    setUploadingKind(kind);
    try {
      const res = await api.uploadImage(file);
      const url = res.secure_url || res.url;
      if (kind === 'avatar') set('avatar', url);
      else if (kind === 'cover') set('cover_url', url);
      else if (kind === 'gallery') setForm(f => ({ ...f, gallery: [...(f.gallery || []), url].slice(0, 6) }));
    } catch (e) {
      toast({ title: 'No pudimos subir la imagen', description: friendlyError(e), variant: 'destructive' });
    } finally { setUploadingKind(null); }
  };

  const onFileChange = (kind) => (e) => {
    const files = Array.from(e.target.files || []);
    if (kind === 'gallery') files.forEach(f => upload(f, 'gallery'));
    else if (files[0]) upload(files[0], kind);
    e.target.value = '';
  };

  const removeGalleryAt = (i) => setForm(f => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));

  const save = async () => {
    v.reset();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.years_experience !== '') payload.years_experience = parseInt(payload.years_experience);
      else delete payload.years_experience;
      Object.keys(payload).forEach(k => {
        if (payload[k] === '' || payload[k] == null) delete payload[k];
      });
      const updated = await api.updateAgentMe(payload);
      onSaved(updated);
      toast({ title: 'Cambios guardados', description: `Perfil ${updated.completeness?.pct || 0}% completo` });
    } catch (e) {
      v.applyError(e, 'No pudimos guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="agent-info-form">
      <CompletenessCard completeness={initial.completeness} testid="agent-completeness-card" />

      <FormBanner msg={v.banner} testid="agent-info-banner" />

      {/* Branding */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="font-serif text-lg font-bold text-habita-dark mb-4">Branding personal</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <ImageUploader
            label="Foto profesional"
            current={form.avatar}
            uploading={uploadingKind === 'avatar'}
            onFile={onFileChange('avatar')}
            onClear={() => set('avatar', '')}
            testid="agent-avatar-uploader"
            aspect="aspect-square"
            rounded="rounded-full"
            helper="Cuadrada · mín. 400×400px"
          />
          <ImageUploader
            label="Banner (portada)"
            current={form.cover_url}
            uploading={uploadingKind === 'cover'}
            onFile={onFileChange('cover')}
            onClear={() => set('cover_url', '')}
            testid="agent-cover-uploader"
            aspect="aspect-[16/6]"
            helper="Horizontal · 1600×500px"
          />
          <div className="hidden md:block" />
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="agent-gallery-section">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-serif text-lg font-bold text-habita-dark">Galería ({form.gallery.length}/6)</h3>
          <label className={`text-xs font-semibold px-3 h-9 rounded-full bg-habita-mint text-habita cursor-pointer flex items-center gap-1 ${form.gallery.length >= 6 ? 'opacity-50 pointer-events-none' : ''}`} data-testid="agent-gallery-add-btn">
            <Plus className="w-3 h-3" /> Agregar foto
            <input type="file" accept="image/*" multiple className="hidden" onChange={onFileChange('gallery')} />
          </label>
        </div>
        {form.gallery.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">Sube fotos de tu oficina, entregas, eventos o momentos con clientes (recomendado 3-6).</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
            {form.gallery.map((url, i) => (
              <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-habita-cream group" data-testid={`agent-gallery-item-${i}`}>
                <img src={url} alt={`Galería ${i + 1}`} className="w-full h-full object-cover" />
                <button onClick={() => removeGalleryAt(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`agent-gallery-remove-${i}`}>
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
            {uploadingKind === 'gallery' && <div className="aspect-square rounded-lg bg-habita-cream flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-habita" /></div>}
          </div>
        )}
      </div>

      {/* Video */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <h3 className="font-serif text-lg font-bold text-habita-dark mb-3">Video de presentación</h3>
        <Label className="text-xs uppercase tracking-widest text-gray-500">URL (YouTube, Vimeo o link MP4)</Label>
        <Input value={form.video_url} onChange={e => set('video_url', e.target.value)} placeholder="https://youtube.com/watch?v=..." className="mt-1.5 h-11" data-testid="agent-edit-video_url" />
        <p className="text-xs text-gray-500 mt-1.5">Próximamente: subida directa de video.</p>
      </div>

      {/* Professional info */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-serif text-lg font-bold text-habita-dark">Información profesional</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="Nombre" k="name" form={form} set={set} v={v} testid="agent-edit-name" />
          <TextField label="Cargo / Título" k="title" form={form} set={set} v={v} placeholder="Asesor Senior, Director Comercial..." testid="agent-edit-title" />
          <TextField label="Ciudad principal" k="city" form={form} set={set} v={v} testid="agent-edit-city" />
          <TextField label="Años de experiencia" k="years_experience" type="number" form={form} set={set} v={v} testid="agent-edit-years_experience" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Descripción breve</Label>
          <textarea value={form.bio} onChange={e => set('bio', e.target.value)} rows={3} maxLength={600} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-habita" data-testid="agent-edit-bio" placeholder="Un párrafo corto que aparece bajo tu nombre." />
          <FormError msg={v.fieldErrors.bio} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-gray-500">Mi trayectoria</Label>
          <textarea value={form.history} onChange={e => set('history', e.target.value)} rows={5} maxLength={4000} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-habita" data-testid="agent-edit-history" placeholder="Comparte tu camino: cómo iniciaste, logros, propiedades emblemáticas, valores..." />
          <FormError msg={v.fieldErrors.history} />
        </div>
        <TagsField label="Especialidades" testid="agent-tags-specialties" value={form.specialties} onChange={val => set('specialties', val)} placeholder="Ej: Apartamentos premium, Inversionistas extranjeros..." />
        <TagsField label="Ciudades donde trabajo" testid="agent-tags-cities_operating" value={form.cities_operating} onChange={val => set('cities_operating', val)} placeholder="Bogotá, Chía, Medellín..." />
        <TagsField label="Idiomas" testid="agent-tags-languages" value={form.languages} onChange={val => set('languages', val)} placeholder="Español, Inglés, Portugués..." />
      </div>

      {/* Contact */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
        <h3 className="font-serif text-lg font-bold text-habita-dark">Contacto y redes</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <TextField label="WhatsApp" k="whatsapp" form={form} set={set} v={v} placeholder="+57 300 000 0000" testid="agent-edit-whatsapp" />
          <TextField label="Teléfono" k="phone" form={form} set={set} v={v} testid="agent-edit-phone" />
          <TextField label="Email" k="email" form={form} set={set} v={v} testid="agent-edit-email" />
          <div className="sm:col-span-2 grid sm:grid-cols-2 gap-3">
            {['instagram', 'facebook', 'linkedin', 'tiktok'].map(s => (
              <div key={s}>
                <Label className="text-xs uppercase tracking-widest text-gray-500 capitalize">{s}</Label>
                <Input value={form.social[s] || ''} onChange={e => set('social', { ...form.social, [s]: e.target.value })} placeholder={`https://${s}.com/...`} className="mt-1.5 h-11" data-testid={`agent-social-${s}`} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Metrics live in <AgentMetricsSection /> above the form */}

      <Button onClick={save} disabled={saving || !!uploadingKind} className="btn-habita rounded-full px-8 h-12 w-full sm:w-auto" data-testid="agent-save-btn">
        <Save className="w-4 h-4 mr-1.5" /> {saving ? 'Guardando...' : 'Guardar cambios'}
      </Button>
    </div>
  );
}

function AgentMetricsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [windowDays, setWindowDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    api.getAgentMetrics(windowDays)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [windowDays]);

  return (
    <div className="space-y-5 mb-6" data-testid="agent-metrics-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-serif text-xl font-bold text-habita-dark">Mis estadísticas</h2>
        <div className="flex gap-1 rounded-full bg-white border border-gray-200 p-1" data-testid="metrics-window-switch">
          {[7, 30, 90].map(d => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              className={`px-3 h-8 text-xs font-semibold rounded-full transition-colors ${windowDays === d ? 'bg-habita text-white' : 'text-gray-600 hover:text-habita-dark'}`}
              data-testid={`metrics-window-${d}`}
            >
              {d} días
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
      {!loading && data && (
        <>
          <KpiGrid
            kpis={data.kpis}
            order={['profile_views', 'property_views', 'whatsapp_clicks', 'phone_clicks', 'favorites', 'shares', 'contacts', 'visit_requests']}
            testid="agent-kpis"
          />
          <SeriesChart series={data.series} testid="agent-series-chart" />
          <ActivityFeed scope="mine" title="Actividad reciente" limit={15} testid="agent-activity-feed" />
        </>
      )}
    </div>
  );
}

function TextField({ label, k, type = 'text', form, set, v, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-gray-500">{label}</Label>
      <Input
        type={type}
        value={form[k]}
        onChange={e => set(k, e.target.value)}
        placeholder={placeholder}
        className={`mt-1.5 h-11 ${v.errorClass(k)}`}
        data-testid={testid || `agent-edit-${k}`}
        {...v.field(k)}
      />
      <FormError msg={v.fieldErrors[k]} />
    </div>
  );
}
